import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import {
  Car,
  Plane,
  Sparkles,
  Shield,
  Clock,
  CheckCircle2,
  ChevronRight,
  MapPin,
  Phone,
} from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';

const fadeUp = {
  initial: { opacity: 0, y: 40 },
  whileInView: { opacity: 1, y: 0 },
  transition: { duration: 0.8, ease: [0.22, 1, 0.36, 1] },
};

export const Services = () => {
  const { t } = useLanguage();

  const services = [
    {
      id: 'valet',
      icon: Car,
      ...t.servicesPage.valet,
      image: 'https://images.unsplash.com/photo-1549317661-bd32c8ce0db2?q=80&w=2070&auto=format&fit=crop',
    },
    {
      id: 'shuttle',
      icon: Plane,
      ...t.servicesPage.shuttle,
      image: 'https://images.unsplash.com/photo-1544620347-c4fd4a3d5957?q=80&w=2069&auto=format&fit=crop',
    },
    {
      id: 'autopflege',
      icon: Sparkles,
      ...t.servicesPage.carcare,
      image: 'https://images.unsplash.com/photo-1607860108855-64acf2078ed9?q=80&w=2071&auto=format&fit=crop',
    },
  ];

  const benefitIcons = [MapPin, Shield, Clock, CheckCircle2, Car, Sparkles];

  return (
    <div className="overflow-hidden pt-20" data-testid="services-page">
      {/* Hero Section */}
      <section className="py-24 md:py-32">
        <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-7xl">
          <motion.div {...fadeUp} viewport={{ once: true }} className="text-center max-w-3xl mx-auto mb-16">
            <span className="text-[#D4AF37] text-sm font-medium uppercase tracking-widest mb-4 block">
              {t.servicesPage.subtitle}
            </span>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold tracking-tight mb-6">
              {t.servicesPage.title1}{' '}
              <span className="text-[#D4AF37]">{t.servicesPage.title2}</span>
            </h1>
            <p className="text-white/60 text-base md:text-lg leading-relaxed">
              {t.servicesPage.description}
            </p>
          </motion.div>
        </div>
      </section>

      {/* Services Detail */}
      {services.map((service, index) => (
        <section
          key={service.id}
          id={service.id}
          className={`py-24 md:py-32 ${index % 2 === 0 ? 'bg-[#0A0A0A]' : ''}`}
          data-testid={`service-${service.id}`}
        >
          <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-7xl">
            <div
              className={`grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center ${
                index % 2 !== 0 ? 'lg:flex-row-reverse' : ''
              }`}
            >
              <motion.div
                {...fadeUp}
                viewport={{ once: true }}
                className={index % 2 !== 0 ? 'lg:order-2' : ''}
              >
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-12 h-12 rounded-xl bg-[#D4AF37]/10 flex items-center justify-center">
                    <service.icon className="w-6 h-6 text-[#D4AF37]" />
                  </div>
                  <span className="text-[#D4AF37] text-sm font-medium uppercase tracking-widest">
                    {service.subtitle}
                  </span>
                </div>

                <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold tracking-tight mb-6">
                  {service.title}
                </h2>
                <p className="text-white/60 text-base md:text-lg leading-relaxed mb-8">
                  {service.description}
                </p>

                <ul className="space-y-4 mb-8">
                  {service.features.map((feature, i) => (
                    <li key={i} className="flex items-start gap-3">
                      <CheckCircle2 className="w-5 h-5 text-[#D4AF37] flex-shrink-0 mt-0.5" />
                      <span className="text-white/80 text-sm">{feature}</span>
                    </li>
                  ))}
                </ul>

                <div className="flex flex-col sm:flex-row gap-4">
                  <a
                    href="https://mcdgroup.de/buchungssystem/index.php"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center justify-center gap-2 bg-[#D4AF37] text-black px-8 py-4 rounded-full font-semibold hover:bg-[#E5C34A] transition-all duration-300 gold-glow"
                    data-testid={`book-${service.id}-btn`}
                  >
                    {t.nav.bookNow}
                    <ChevronRight className="w-5 h-5" />
                  </a>
                  <Link
                    to="/preise"
                    className="inline-flex items-center justify-center gap-2 border border-white/20 text-white px-8 py-4 rounded-full font-medium hover:border-[#D4AF37] hover:text-[#D4AF37] transition-all"
                  >
                    {t.servicesPage.viewPrices}
                  </Link>
                </div>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, scale: 0.95 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 0.8 }}
                className={index % 2 !== 0 ? 'lg:order-1' : ''}
              >
                <div className="aspect-[4/3] rounded-2xl overflow-hidden">
                  <img
                    src={service.image}
                    alt={service.title}
                    className="w-full h-full object-cover"
                  />
                </div>
              </motion.div>
            </div>
          </div>
        </section>
      ))}

      {/* Process Section */}
      <section className="py-24 md:py-32 bg-[#0A0A0A]">
        <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-7xl">
          <motion.div {...fadeUp} viewport={{ once: true }} className="text-center mb-16">
            <span className="text-[#D4AF37] text-sm font-medium uppercase tracking-widest mb-4 block">
              {t.servicesPage.processSubtitle}
            </span>
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold tracking-tight">
              {t.servicesPage.processTitle}
            </h2>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {t.servicesPage.process.map((item, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="relative"
              >
                <div className="text-6xl font-bold text-[#D4AF37]/10 mb-4">{item.step}</div>
                <h3 className="text-xl font-semibold text-white mb-3">{item.title}</h3>
                <p className="text-white/60 text-sm leading-relaxed">{item.desc}</p>
                
                {index < t.servicesPage.process.length - 1 && (
                  <div className="hidden lg:block absolute top-8 left-full w-full h-px bg-gradient-to-r from-[#D4AF37]/30 to-transparent" />
                )}
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits Grid */}
      <section className="py-24 md:py-32">
        <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-7xl">
          <motion.div {...fadeUp} viewport={{ once: true }} className="text-center mb-16">
            <span className="text-[#D4AF37] text-sm font-medium uppercase tracking-widest mb-4 block">
              {t.servicesPage.benefitsSubtitle}
            </span>
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold tracking-tight">
              {t.servicesPage.benefitsTitle}
            </h2>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {t.servicesPage.benefitsList.map((benefit, index) => {
              const Icon = benefitIcons[index] || CheckCircle2;
              return (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 40 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                  className="bg-black/40 backdrop-blur-sm border border-white/10 rounded-2xl p-8 hover:border-[#D4AF37]/30 transition-all duration-500"
                >
                  <div className="w-12 h-12 rounded-xl bg-[#D4AF37]/10 flex items-center justify-center mb-6">
                    <Icon className="w-6 h-6 text-[#D4AF37]" />
                  </div>
                  <h3 className="text-lg font-semibold text-white mb-3">{benefit.title}</h3>
                  <p className="text-white/60 text-sm leading-relaxed">{benefit.desc}</p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 md:py-32 bg-[#0A0A0A]">
        <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-7xl">
          <motion.div
            {...fadeUp}
            viewport={{ once: true }}
            className="relative rounded-3xl overflow-hidden"
          >
            <div
              className="absolute inset-0 bg-cover bg-center"
              style={{
                backgroundImage: `url('https://images.unsplash.com/photo-1436491865332-7a61a109cc05?q=80&w=2074&auto=format&fit=crop')`,
              }}
            />
            <div className="absolute inset-0 bg-black/70" />

            <div className="relative z-10 px-8 py-16 md:px-16 md:py-24 text-center">
              <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold tracking-tight mb-6">
                {t.servicesPage.ctaTitle}
              </h2>
              <p className="text-white/70 text-base md:text-lg max-w-2xl mx-auto mb-8">
                {t.servicesPage.ctaDesc}
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <a
                  href="https://mcdgroup.de/buchungssystem/index.php"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center justify-center gap-2 bg-[#D4AF37] text-black px-10 py-5 rounded-full font-semibold text-lg hover:bg-[#E5C34A] transition-all duration-300 gold-glow"
                  data-testid="services-cta-btn"
                >
                  {t.servicesPage.ctaBtn}
                  <ChevronRight className="w-5 h-5" />
                </a>
                <a
                  href="tel:+4915126777707"
                  className="inline-flex items-center justify-center gap-2 border border-white/30 text-white px-10 py-5 rounded-full font-medium hover:border-[#D4AF37] hover:text-[#D4AF37] transition-all"
                >
                  <Phone className="w-5 h-5" />
                  {t.servicesPage.callBtn}
                </a>
              </div>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};
